import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { gsap } from 'gsap';
import { categories, getProductsByCategory, Product } from '../data/menuData.ts';
import LazyImage from '../components/LazyImage.tsx';
import { SHOW_MENU_PRICES } from '../config/ui.ts';

const Container = styled.div`
  min-height: 100vh;
  background: var(--white);
`;

const Header = styled.header`
  background: var(--navy-blue);
  color: var(--white);
  padding: 1rem;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TableInfo = styled.div`
  h2 {
    margin: 0;
    font-size: 1.5rem;
  }
  
  p {
    margin: 0;
    opacity: 0.9;
    font-size: 0.9rem;
  }
`;

const CartButton = styled.button`
  background: var(--primary-turquoise);
  color: var(--navy-blue);
  border: none;
  padding: 0.5rem 1.5rem;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const CartCount = styled.span`
  background: #ff6b6b;
  color: var(--white);
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  position: absolute;
  top: -8px;
  right: -8px;
`;

const MainContent = styled.main`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const CategoryTabs = styled.div`
  display: flex;
  gap: 1rem;
  overflow-x: auto;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: #e0e0e0;
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--primary-turquoise);
    border-radius: 4px;
  }
`;

const CategoryTab = styled.button<{ active: boolean }>`
  background: ${props => props.active ? 'var(--primary-turquoise)' : 'var(--white)'};
  color: ${props => props.active ? 'var(--white)' : 'var(--navy-blue)'};
  border: 2px solid ${props => props.active ? 'var(--primary-turquoise)' : '#e0e0e0'};
  padding: 0.8rem 1.5rem;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: var(--primary-turquoise);
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const ProductCard = styled.div`
  background: var(--white);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 5px 20px rgba(0,0,0,0.15);
  }
`;

const ProductImage = styled.div`
  height: 180px;
  background: var(--gradient-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  color: var(--white);
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ProductContent = styled.div`
  padding: 1.5rem;
`;

const ProductHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 0.5rem;
`;

const ProductName = styled.h3`
  margin: 0;
  font-size: 1.2rem;
  color: var(--navy-blue);
`;

const ProductPrice = styled.div`
  font-size: 1.3rem;
  font-weight: bold;
  color: var(--primary-turquoise);
`;

const ProductDescription = styled.p`
  color: #666;
  font-size: 0.9rem;
  margin: 0.5rem 0 1rem 0;
  line-height: 1.4;
`;

const AddToCartButton = styled.button`
  width: 100%;
  background: var(--primary-turquoise);
  color: var(--white);
  border: none;
  padding: 0.8rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #2db3a3;
  }
`;

const OrderSummary = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--white);
  box-shadow: 0 -5px 20px rgba(0,0,0,0.1);
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 90;
`;

interface CartItem extends Product {
  quantity: number;
}

const TableOrderPage: React.FC = () => {
  const { locationId, tableId } = useParams<{ locationId: string; tableId: string }>();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState(categories[0].id);
  const [cart, setCart] = useState<CartItem[]>([]);

  useEffect(() => {
    // Animaciones GSAP al cargar
    gsap.fromTo('.product-card', 
      { opacity: 0, y: 20 },
      { 
        opacity: 1, 
        y: 0, 
        duration: 0.5, 
        stagger: 0.1,
        ease: 'power2.out'
      }
    );
  }, [activeCategory]);

  const addToCart = (product: Product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item => 
          item.id === product.id 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });

    // Animación de feedback
    gsap.fromTo(`#product-${product.id}`,
      { scale: 1 },
      { scale: 1.05, duration: 0.2, yoyo: true, repeat: 1 }
    );
  };

  const getTotalItems = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const goToCart = () => {
    navigate(`/order/${locationId}/${tableId}/cart`);
  };

  return (
    <Container>
      <Header>
        <HeaderContent>
          <TableInfo>
            <h2>Mesa {tableId}</h2>
            <p>Pepe Tiburón - {locationId}</p>
          </TableInfo>
          
          <CartButton onClick={goToCart}>
            <i className="fas fa-shopping-cart"></i>
            Ver pedido
            {getTotalItems() > 0 && <CartCount>{getTotalItems()}</CartCount>}
          </CartButton>
        </HeaderContent>
      </Header>

      <MainContent>
        <CategoryTabs>
          {categories.map(category => (
            <CategoryTab
              key={category.id}
              active={activeCategory === category.id}
              onClick={() => setActiveCategory(category.id)}
            >
              {category.name}
            </CategoryTab>
          ))}
        </CategoryTabs>

        <ProductsGrid>
          {getProductsByCategory(activeCategory).map((product, index) => (
            <ProductCard
              key={product.id}
              id={`product-${product.id}`}
              className="product-card"
              style={{ opacity: 0 }}
            >
              <ProductImage>
                <LazyImage
                  src={product.image}
                  fallbackSrc={`${process.env.PUBLIC_URL}/logo.png`}
                  alt={product.name}
                  width="100%"
                  height="100%"
                  objectFit="cover"
                  placeholder="fas fa-image"
                />
              </ProductImage>
              <ProductContent>
                <ProductHeader>
                  <ProductName>{product.name}</ProductName>
                  {SHOW_MENU_PRICES && (
                    <ProductPrice>S/. {product.price.toFixed(2)}</ProductPrice>
                  )}
                </ProductHeader>
                <ProductDescription>{product.description}</ProductDescription>
                <AddToCartButton onClick={() => addToCart(product)}>
                  <i className="fas fa-plus"></i> Agregar al pedido
                </AddToCartButton>
              </ProductContent>
            </ProductCard>
          ))}
        </ProductsGrid>
      </MainContent>

      {cart.length > 0 && (
        <OrderSummary>
          <div>
            <strong>{getTotalItems()} items</strong>
            <div>
              Total:{' '}
              {SHOW_MENU_PRICES ? `S/. ${getTotalPrice().toFixed(2)}` : 'Consultar'}
            </div>
          </div>
          <CartButton onClick={goToCart}>
            Ver pedido
            <i className="fas fa-arrow-right"></i>
          </CartButton>
        </OrderSummary>
      )}
    </Container>
  );
};

export default TableOrderPage;